/**
 * THE SHOP'S REVIEWS, ALL OF THEM, IN ONE SEARCHABLE LIST.
 *
 * The owner, 2026-10-14: *"The full reviews view isn't good. Why not use our
 * components for that? Not filterable, not searchable. Mark that it is
 * imported from Google (with a Google icon or something). Make sure it
 * merges these reviews with Stylon reviews."*
 *
 * It used to be a plain column of cards — correct for a shop with three
 * transcribed reviews, useless for dk-store with eighty-six. So it now wears
 * the SAME controls the product reviews sheet does, from the same files:
 * `ReviewsFilterBar` (search · Sort by · Rating, and the two menus),
 * `ReviewsHistogram` and `useReviewsControls`. Not a copy of them — the
 * copy is the thing this change exists to avoid.
 *
 * ── ONE LIST, EVERY SOURCE ────────────────────────────────────────────────
 *
 * `GET /v1/public/salons/:slug/shop-reviews` has ALWAYS answered with the
 * sources merged, newest first: `STYLON` (left by a customer here),
 * `GOOGLE_TRANSCRIBED` (copied by the business from its own Google listing)
 * and `OWNER_ENTERED`. What was missing was any way to tell them apart or
 * to ask for one of them, so a mixed shop read as an undifferentiated
 * column. The chip row above the search is that question, with each
 * source's count taken from the payload's `sources` block — never counted
 * off the loaded page, which is the filtered thing.
 *
 * A CHIP EXISTS ONLY WHEN THERE IS SOMETHING TO CHOOSE BETWEEN. A shop whose
 * reviews are all from Google gets no row at all rather than a "Stylon 0"
 * chip that can only disappoint; the count line above still says "on
 * Google", which is the same fact in words.
 *
 * ── WHAT IS SORTED WHERE ──────────────────────────────────────────────────
 *
 * The endpoint has no sort, search or filter parameters at all, so every one
 * of the four sorts, the search and both filters are applied here, over the
 * page the server returned (we ask for 200, its cap). A shop with more than
 * 200 reviews would therefore search only the first 200 — a server gap,
 * written down rather than hidden.
 *
 * Class names avoid `panel`, `popup`, `overlay` and `modal` + `box`/`content`
 * for the reason `ReviewsModal` gives: `src/modal-fixes.css` matches those
 * inside any `[role="dialog"]` and swallows the wheel on a sheet with
 * nothing to scroll.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import AnimatedModal from '../../Components/AnimatedModal';
import { listShopReviews } from '../../api/storeApi';
import { StarsRow } from './ReviewCard';
import ShopReviewCard, { allFromGoogle, shopReviewsCountLine } from './ShopReviewCard';
import ReviewsFilterBar, { ReviewsHistogram } from './ReviewsFilterBar';
import useReviewsControls from './useReviewsControls';
import GoogleMark from './GoogleMark';
import modalStyles from './reviewsModal.module.css';
import styles from './shopReviewsBlock.module.css';

const PAGE = 200;

const at = (r) => new Date(r?.reviewedAt || 0).getTime() || 0;

/**
 * No `apiSort` on any row, unlike the product sheet's: this endpoint takes
 * no sort at all, so all four are ours. "Newest" still carries a comparator
 * rather than trusting the server's order — the sheet should not change
 * meaning if that order ever does.
 */
const SHOP_SORTS = [
  { value: 'latest', key: 'reviews.sortNewest', label: 'Newest', compare: (a, b) => at(b) - at(a) },
  { value: 'oldest', key: 'reviews.sortOldest', label: 'Oldest', compare: (a, b) => at(a) - at(b) },
  { value: 'stars_desc', key: 'reviews.sortStarsDesc', label: 'Rating: High → Low', compare: (a, b) => (b.rating - a.rating) || (at(b) - at(a)) },
  { value: 'stars_asc', key: 'reviews.sortStarsAsc', label: 'Rating: Low → High', compare: (a, b) => (a.rating - b.rating) || (at(b) - at(a)) },
];
const DEFAULT_SORT = 'latest';

const SOURCE_ORDER = ['GOOGLE_TRANSCRIBED', 'STYLON', 'OWNER_ENTERED'];
const SOURCE_CHIP = {
  GOOGLE_TRANSCRIBED: ['shopReviews.sourceGoogle', 'Google'],
  STYLON: ['shopReviews.sourceStylon', 'Stylon'],
  OWNER_ENTERED: ['shopReviews.sourceShopShort', 'The shop'],
};

const ShopReviewsModal = ({ open = true, salonSlug, shopName, summary, onClose, t, isRtl }) => {
  const { i18n } = useTranslation();
  const locale = i18n?.language;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState('ALL');

  useEffect(() => {
    if (!open || !salonSlug) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.resolve(listShopReviews(salonSlug, { limit: PAGE }))
      .then((d) => { if (!cancelled) setData(d); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, salonSlug]);

  const live = data || summary || {};
  const count = Number(live.count) || 0;
  const average = Number(live.average) || 0;
  const reviews = useMemo(() => (Array.isArray(data?.reviews) ? data.reviews : []), [data]);

  /* THE COUNTS COME FROM THE PAYLOAD, NOT THE PAGE. Counting the loaded rows
   * would make every chip's number change as the shopper filters — which is
   * the one thing a count beside a filter must never do. */
  const sources = live.sources || {};
  const available = SOURCE_ORDER.filter((s) => (Number(sources[s]) || 0) > 0);
  const showSources = available.length > 1;

  const extraFilter = useCallback((r) => source === 'ALL' || r.source === source, [source]);
  const clearExtra = useCallback(() => setSource('ALL'), []);
  const getStars = useCallback((r) => r.rating, []);
  const getSearchParts = useCallback((r) => [r?.authorName, r?.text], []);

  const {
    sort, setSort,
    query, setQuery,
    stars, toggleStar, resetStars,
    clearAll, filtersActive,
    visible,
  } = useReviewsControls({
    rows: reviews,
    sortOptions: SHOP_SORTS,
    defaultSort: DEFAULT_SORT,
    getStars,
    getSearchParts,
    extraFilter,
    extraActive: source !== 'ALL',
    onClearExtra: clearExtra,
  });

  const title = shopName
    ? t('shopReviews.title', { defaultValue: 'What customers say about {{shop}}', shop: shopName })
    : t('shopReviews.titleNoShop', { defaultValue: 'What customers say' });

  const everyOneFromGoogle = allFromGoogle(live);
  /* The list these were copied from — the first row that names it. `http(s)`
   * only: this is an address that arrived in a payload and is about to
   * become an href. */
  const googleSourceUrl = everyOneFromGoogle
    ? (reviews.map((r) => r?.sourceUrl).find((u) => typeof u === 'string' && /^https?:\/\//i.test(u)) || null)
    : null;

  return (
    <AnimatedModal
      open={open}
      onClose={onClose}
      sheetClassName={modalStyles.sheet}
      testId="shop-reviews-modal"
      ariaLabel={title}
    >
      {({ requestClose }) => (
        <div dir={isRtl ? 'rtl' : undefined}>
          <button
            type="button"
            className={modalStyles.closeBtn}
            onClick={requestClose}
            aria-label={t('common.close', { defaultValue: 'Close' })}
            data-testid="shop-reviews-modal-close"
          >
            <FiX />
          </button>

          <div className={modalStyles.header}>
            <h2 className={modalStyles.title}>{title}</h2>
            <div className={modalStyles.avgRow}>
              <span className={modalStyles.avgValue}>{average.toFixed(1)}</span>
              <StarsRow value={average} size={22} t={t} testId="shop-reviews-modal-average-stars" />
              <span className={modalStyles.avgCount} data-testid="shop-reviews-modal-count">
                {shopReviewsCountLine(live, t)}
              </span>
            </div>
            {everyOneFromGoogle && (
              <p className={styles.note} data-testid="shop-reviews-modal-note">
                {t('shopReviews.transcribedNote', {
                  defaultValue: 'Real reviews, written on Google and transcribed by the shop — names and dates as published.',
                })}
              </p>
            )}
            {googleSourceUrl && (
              <a
                className={styles.googleLink}
                href={googleSourceUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
                data-testid="shop-reviews-modal-google-link"
              >
                {t('shopReviews.seeOnGoogle', { defaultValue: 'See our reviews on Google' })}
              </a>
            )}
          </div>

          {/* WHERE THE REVIEW CAME FROM — the first question this list
            * answers, so it sits above the search rather than inside the
            * sort/rating chip row. `role="tablist"` + `aria-selected`, the
            * same shape the shop page's own source chips use. */}
          {showSources && (
            <div
              className={styles.sourceChips}
              role="tablist"
              aria-label={t('shopReviews.sourceFilter', { defaultValue: 'Where the review came from' })}
              data-testid="shop-reviews-modal-sources"
            >
              {['ALL', ...available].map((s) => {
                const [key, fallback] = s === 'ALL'
                  ? ['shopReviews.sourceAll', 'All']
                  : SOURCE_CHIP[s];
                const n = s === 'ALL' ? count : (Number(sources[s]) || 0);
                return (
                  <button
                    key={s}
                    type="button"
                    role="tab"
                    aria-selected={source === s}
                    className={`${modalStyles.chip} ${source === s ? modalStyles.chipOn : ''}`}
                    onClick={() => setSource(s)}
                    data-testid={`shop-reviews-modal-source-${s}`}
                  >
                    {s === 'GOOGLE_TRANSCRIBED' && <GoogleMark className={styles.sourceMark} />}
                    <span>{t(key, { defaultValue: fallback })}</span>
                    <span className={modalStyles.chipCount}>{n}</span>
                  </button>
                );
              })}
            </div>
          )}

          <ReviewsFilterBar
            t={t}
            testIdPrefix="shop-reviews-modal"
            query={query}
            onQuery={setQuery}
            searchAria={t('reviews.searchAria', {
              defaultValue: 'Search reviews for {{name}}',
              name: shopName || '',
            })}
            sort={sort}
            onSort={setSort}
            sortOptions={SHOP_SORTS}
            defaultSort={DEFAULT_SORT}
            stars={stars}
            onToggleStar={toggleStar}
            onResetStars={resetStars}
            histogram={live.histogram}
            filtersActive={filtersActive}
            onClearAll={clearAll}
          />

          <ReviewsHistogram
            histogram={live.histogram}
            stars={stars}
            onToggleStar={toggleStar}
            t={t}
            testIdPrefix="shop-reviews-modal"
          />

          <div className={modalStyles.list} data-testid="shop-reviews-modal-list" aria-busy={loading}>
            {visible.map((r) => (
              <ShopReviewCard key={r.id} review={r} t={t} locale={locale} testIdPrefix="shop-reviews-modal-review" />
            ))}
          </div>

          {!loading && visible.length === 0 && (
            <p
              className={modalStyles.emptyMsg}
              data-testid={reviews.length ? 'shop-reviews-modal-no-results' : 'shop-reviews-modal-empty'}
            >
              {reviews.length
                ? t('reviews.noMatches', { defaultValue: 'No reviews match these filters.' })
                : t('shopReviews.empty', { defaultValue: 'No reviews yet.' })}
            </p>
          )}
        </div>
      )}
    </AnimatedModal>
  );
};

export default ShopReviewsModal;
